import type { VariantOptions } from '../types/Products';
import { compact, uniq } from './arrays';

type NormalisedVariant = {
  variant: VariantProduct;
  colourway?: VariantColourway;
  colourwayId?: string;
  composition?: VariantComposition;
  compositionId?: string;
  dimension?: VariantDimension;
  dimensionId?: string;
  handFinished?: boolean;
  handFinishedId?: string;
  size?: VariantSize;
  sizeId?: string;
};

type VariantQuery = {
  colourwayId?: string;
  compositionId?: string;
  dimensionId?: string;
  handFinishedId?: string;
  sizeId?: string;
};

export const getVariantOptions = (
  product: Pick<ProductType, 'productVariants' | 'medium' | 'title'>,
): VariantOptions => {
  const variants = getProductVariants(product);
  const config = product.productVariants[0];
  if (config === undefined || variants === null) {
    return {
      colourways: [],
      compositions: [],
      dimensions: [],
      finishing: [],
      sizes: [],
    };
  }

  const normalisedVariants = compact(variants.map(normaliseVariant));
  const activeQuery: VariantQuery = {
    colourwayId: colourwayId(config.variantColourway?.[0]),
    compositionId: compositionId(config.variantComposition?.[0]),
    dimensionId: dimensionId(config.variantDimension?.[0]),
    handFinishedId: handFinishedId(product.medium?.includes('hand')),
    sizeId: sizeId(config.variantSize?.[0]),
  };

  return {
    colourways: colourways(normalisedVariants, activeQuery),
    compositions: compositions(normalisedVariants, activeQuery),
    dimensions: dimensions(normalisedVariants, activeQuery),
    finishing: finishings(normalisedVariants, activeQuery),
    sizes: sizes(normalisedVariants, activeQuery),
  };
};

const sizes = (variants: NormalisedVariant[], query: VariantQuery) => {
  const allSizeOptions = compact(uniq(variants.map((v) => v.sizeId)));

  return compact(
    allSizeOptions.map((sizeId) => {
      const variant = findMatchingVariant(variants, {
        ...query,
        sizeId,
      });

      if (variant?.size === undefined) return null;

      return {
        size: variant.size,
        variant: variant.variant,
      };
    }),
  );
};

const colourways = (variants: NormalisedVariant[], query: VariantQuery) => {
  const allColourwayOptions = compact(uniq(variants.map((v) => v.colourwayId)));

  return compact(
    allColourwayOptions.map((colourwayId) => {
      const variant = findMatchingVariant(variants, {
        ...query,
        colourwayId,
      });

      if (variant?.colourway === undefined) return null;

      return {
        colourway: variant.colourway,
        variant: variant.variant,
      };
    }),
  );
};

const compositions = (variants: NormalisedVariant[], query: VariantQuery) => {
  const allCompositionOptions = compact(
    uniq(variants.map((v) => v.compositionId)),
  );

  return compact(
    allCompositionOptions.map((compositionId) => {
      const variant = findMatchingVariant(variants, {
        ...query,
        compositionId,
      });

      if (variant?.composition === undefined) return null;

      return {
        composition: variant.composition,
        variant: variant.variant,
      };
    }),
  );
};

const finishings = (variants: NormalisedVariant[], query: VariantQuery) => {
  const allHandFinishedOptions = compact(
    uniq(variants.map((v) => v.handFinishedId)),
  );

  return compact(
    allHandFinishedOptions.map((handFinishedId) => {
      const variant = findMatchingVariant(variants, {
        ...query,
        handFinishedId,
      });

      if (variant?.handFinished === undefined) return null;

      return {
        handFinished: variant.handFinished,
        variant: variant.variant,
      };
    }),
  );
};

const dimensions = (variants: NormalisedVariant[], query: VariantQuery) => {
  const allDimensionOptions = compact(uniq(variants.map((v) => v.dimensionId)));

  return compact(
    allDimensionOptions.map((dimensionId) => {
      const variant = findMatchingVariant(variants, {
        ...query,
        dimensionId,
      });

      if (variant?.dimension === undefined) return null;

      return {
        dimension: variant.dimension,
        variant: variant.variant,
      };
    }),
  );
};

const findMatchingVariant = (
  variants: NormalisedVariant[],
  query: VariantQuery,
) => {
  return variants.find(
    (v) =>
      v.colourwayId === query.colourwayId &&
      v.compositionId === query.compositionId &&
      v.dimensionId === query.dimensionId &&
      v.handFinishedId === query.handFinishedId &&
      v.sizeId === query.sizeId,
  );
};

const normaliseVariant = (
  variant: VariantProduct,
): NormalisedVariant | null => {
  const config = variant.content.productVariants?.[0];
  if (config === undefined) return null;

  const colourway = config.variantColourway?.[0];
  const composition = config.variantComposition?.[0];
  const dimension = config.variantDimension?.[0];
  const handFinished = variant.content.medium?.includes('hand');
  const size = config.variantSize?.[0];

  return {
    variant,
    colourway,
    colourwayId: colourwayId(colourway),
    composition,
    compositionId: compositionId(composition),
    dimension,
    dimensionId: dimensionId(dimension),
    handFinished,
    handFinishedId: handFinishedId(handFinished),
    size,
    sizeId: sizeId(size),
  };
};

// This ensures live preview works well when editing variants in Storyblok, as
// it prefers the base product variant configuration (which will live update)
// rather than the nested configuration (which won't) for the variant which is
// the product currently being edited.
const getProductVariants = (
  product: Pick<ProductType, 'productVariants' | 'medium' | 'title'>,
): VariantProduct[] | null => {
  const productConfig = product.productVariants[0];
  if (productConfig === undefined) return null;

  return compact(
    productConfig.variants.map((variant): VariantProduct | null => {
      const isProductVariant = variant.content.title === product.title;

      if (isProductVariant) {
        return {
          ...variant,
          content: {
            ...variant.content,
            productVariants: [productConfig],
          },
        };
      }

      return variant;
    }),
  );
};

const colourwayId = (c?: VariantColourway) => c?.colours?.sort()?.join('_');
const compositionId = (c?: VariantComposition) =>
  c?.compositionNumber?.toString();
const dimensionId = (c?: VariantDimension) => c?.dimension.toString();
const handFinishedId = (c?: boolean) => (c ? 'yes' : 'no');
const sizeId = (c?: VariantSize) => c?.size?.toString();
